/**
 * IdleSessionManager — auto-logout after inactivity.
 * ─────────────────────────────────────────────────
 * Behaviour:
 *   • Watches for user activity (mouse, keyboard, touch, scroll).
 *   • After IDLE_MS of no activity, shows a modal asking
 *     "Do you want to continue your session?" with a countdown.
 *   • "Yes, continue" resets the idle timer.
 *   • "No, logout" OR countdown expiring OR modal dismiss → runs `logout()`
 *     and redirects to the appropriate login page (customer/admin).
 *
 * Mounted at the app root inside `<AuthProvider>` — only renders when a
 * user is logged in. Works for every role (admin / vendor / sales /
 * saathi / care / installation / customer).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
    AlertDialogDescription, AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Clock, LogOut, ShieldCheck } from 'lucide-react';

const IDLE_MS       = 10 * 60 * 1000;   // 10 minutes without activity
const COUNTDOWN_SEC = 60;               // 60s to respond to prompt
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];

export default function IdleSessionManager() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [promptOpen, setPromptOpen] = useState(false);
    const [remaining, setRemaining]   = useState(COUNTDOWN_SEC);
    const idleTimerRef      = useRef(null);
    const countdownTimerRef = useRef(null);
    const lastActivityRef   = useRef(Date.now());

    // Clear any pending timers safely.
    const clearTimers = useCallback(() => {
        if (idleTimerRef.current)      { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
        if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
    }, []);

    // Actually terminates the session and bounces to login.
    const doLogout = useCallback(() => {
        clearTimers();
        setPromptOpen(false);
        try { logout(); } catch (_e) { /* noop */ }
        const dest = (user?.role === 'customer' || !user?.role)
            ? '/customer/login'
            : '/admin/login';
        try { nav(dest, { replace: true }); } catch (_e) { window.location.assign(dest); }
    }, [clearTimers, logout, nav, user?.role]);

    // Kick off the 10-min idle countdown; called on every activity ping.
    const scheduleIdleCheck = useCallback(() => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => {
            // Trigger the "Are you still there?" prompt + start 60-sec countdown.
            setRemaining(COUNTDOWN_SEC);
            setPromptOpen(true);
            if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = setInterval(() => {
                setRemaining(prev => {
                    if (prev <= 1) { doLogout(); return 0; }
                    return prev - 1;
                });
            }, 1000);
        }, IDLE_MS);
    }, [doLogout]);

    // Activity → reset idle timer (but not while the prompt is up — that
    // would defeat the purpose of asking the user).
    const onActivity = useCallback(() => {
        if (promptOpen) return;
        lastActivityRef.current = Date.now();
        scheduleIdleCheck();
    }, [promptOpen, scheduleIdleCheck]);

    // Mount / unmount lifecycle
    useEffect(() => {
        if (!user) { clearTimers(); return undefined; }
        scheduleIdleCheck();
        ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, onActivity, { passive: true }));
        return () => {
            clearTimers();
            ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, onActivity));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    // "Yes, continue" — dismiss prompt, restart idle timer.
    const continueSession = () => {
        if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
        setPromptOpen(false);
        lastActivityRef.current = Date.now();
        scheduleIdleCheck();
    };

    if (!user) return null;

    const pct = (remaining / COUNTDOWN_SEC) * 100;

    return (
        <AlertDialog open={promptOpen} onOpenChange={(o) => { if (!o) continueSession(); }}>
            <AlertDialogContent data-testid='idle-session-prompt' className='max-w-md'>
                <AlertDialogHeader>
                    <AlertDialogTitle className='flex items-center gap-2'>
                        <Clock className='w-5 h-5 text-amber-500' />
                        Do you want to continue your session?
                    </AlertDialogTitle>
                    <AlertDialogDescription className='space-y-2 pt-2'>
                        <span className='block'>
                            आप 10 minutes से inactive हैं। Security के लिए हम automatically logout कर देंगे।
                        </span>
                        <span className='block text-xs text-muted-foreground'>
                            10 minutes se koi activity nahi hui. Kya aap logged in rehna chahenge?
                        </span>
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className='space-y-1.5 pt-1'>
                    <div className='flex items-center justify-between text-[12px]'>
                        <span className='text-muted-foreground'>Auto-logout in</span>
                        <span className='font-mono font-semibold tabular-nums text-primary' data-testid='idle-countdown'>
                            {String(Math.floor(remaining / 60)).padStart(1, '0')}:{String(remaining % 60).padStart(2, '0')}
                        </span>
                    </div>
                    <Progress value={pct} className='h-1.5' />
                </div>

                <AlertDialogFooter className='mt-3'>
                    <Button variant='outline' onClick={doLogout} data-testid='idle-logout-btn'>
                        <LogOut className='w-4 h-4 mr-1.5' /> No, Logout
                    </Button>
                    <Button onClick={continueSession} data-testid='idle-continue-btn'>
                        <ShieldCheck className='w-4 h-4 mr-1.5' /> Yes, Continue
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
